// Is the current user an admin?
//
// Five components each answered this separately by reading adminUsers/{uid},
// which meant five database round-trips and five subtly different fallbacks.
// One place now, and it prefers the ID token's custom claim.
//
// The claim matters beyond tidiness: security rules can read auth.token.admin
// directly instead of doing a root.child() lookup per rule, and admin status
// becomes provable outside the database.
//
// adminUsers/{uid} is still the source of record. The claim is a mirror of it,
// kept in step by the onAdminUserWritten trigger.

import { db, auth } from './firebase.js';

let cached = null;   // { uid, isAdmin }
let inFlight = null;

/**
 * @param {object} [options]
 * @param {boolean} [options.force] re-check, ignoring the cache
 */
export async function isAdmin({ force = false } = {}) {
  const user = auth.currentUser;
  if (!user) return false;

  if (!force && cached && cached.uid === user.uid) return cached.isAdmin;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    let result = false;
    try {
      // A token issued before the claim was granted still carries the old
      // claims for up to an hour, so refresh when asked to re-check.
      const token = await user.getIdTokenResult(force);
      if (token.claims && token.claims.admin === true) {
        result = true;
      } else {
        // Fall back to the source of record. This keeps working for users whose
        // claim has not been issued yet, and is the only path until the
        // reconcile has run once.
        const snap = await db.ref(`adminUsers/${user.uid}`).once('value');
        result = snap.exists();
      }
    } catch (error) {
      console.error('Admin check failed; treating as non-admin:', error);
      result = false;
    }
    cached = { uid: user.uid, isAdmin: result };
    inFlight = null;
    return result;
  })();

  return inFlight;
}

/** Drop the cache. Call on sign-out so the next user is not judged by the last one's answer. */
export function resetAdminCache() {
  cached = null;
  inFlight = null;
}

/**
 * Force the ID token to pick up a claim granted since sign-in, without making
 * the user sign out and back in.
 */
export async function refreshAdminClaim() {
  if (!auth.currentUser) return false;
  await auth.currentUser.getIdToken(true);
  return isAdmin({ force: true });
}
